import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, IdCell, Input, KV, Modal, PageHead, SearchBox,
  Select, StatCard, Tabs, Textarea, dateTimeStr, inr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { claimApi } from '../api';
import { useToast } from '../context/ToastContext';

const TABS = [
  { value: '', label: 'All' },
  { value: 'raised,under_review', label: 'Open' },
  { value: 'verified', label: 'Verified' },
  { value: 'approved', label: 'Approved' },
  { value: 'settled', label: 'Settled' },
  { value: 'rejected', label: 'Rejected' },
];

/** Legal forward transitions, mirroring the server's claim flow. */
const NEXT = {
  raised: ['under_review', 'rejected'],
  under_review: ['verified', 'rejected'],
  verified: ['approved', 'rejected'],
  approved: [],
  settled: [],
  rejected: [],
};

const Claims = () => {
  const toast = useToast();
  const list = useList(claimApi.list, { status: '', type: '', search: '' });

  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null); // { claim, status }
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [decision, setDecision] = useState('refund');
  const [settle, setSettle] = useState(null);
  const [settleForm, setSettleForm] = useState({ amount: '', mode: 'bank_transfer', reference: '' });
  const [busy, setBusy] = useState(false);

  const loadStats = () => claimApi.stats().then(res => setStats(res.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const openDetail = async row => {
    try {
      setDetail((await claimApi.get(row._id)).data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submitAction = async () => {
    setBusy(true);
    try {
      await claimApi.setStatus(action.claim._id, {
        status: action.status,
        note,
        decision: action.status === 'approved' ? decision : undefined,
        approvedAmount: action.status === 'approved' ? Number(amount) || undefined : undefined,
      });
      toast.success(`Claim ${action.status.replace('_', ' ')}`);
      setAction(null);
      setNote('');
      setDetail(null);
      list.reload();
      loadStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitSettle = async () => {
    setBusy(true);
    try {
      await claimApi.settle(settle._id, { ...settleForm, amount: Number(settleForm.amount) });
      toast.success('Claim settled');
      setSettle(null);
      setDetail(null);
      list.reload();
      loadStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'claimNumber',
      label: 'Claim',
      render: row => (
        <>
          <div className="cell-strong mono">{row.claimNumber}</div>
          <div className="cell-muted">{dateTimeStr(row.createdAt)}</div>
        </>
      ),
    },
    { key: 'type', label: 'Type', render: row => <Badge status={row.type} tone="warn" dot={false}>{titleCase(row.type)}</Badge> },
    { key: 'product', label: 'Product', render: row => <IdCell image={row.product?.thumbnail} name={row.product?.name} sub={`${row.affectedQuantity} unit(s)`} size="sm" /> },
    {
      key: 'parties',
      label: 'Raised by → Against',
      render: row => (
        <>
          <div>{row.raisedBy?.name}</div>
          <div className="cell-muted">→ {row.against?.name}</div>
        </>
      ),
    },
    { key: 'claimAmount', label: 'Claimed', align: 'right', render: row => <strong>{inr(row.claimAmount)}</strong> },
    {
      key: 'insured',
      label: 'Insured',
      render: row => (row.isInsured ? <Badge status="active" dot={false}>Insured</Badge> : <span className="text-muted text-sm">—</span>),
    },
    { key: 'status', label: 'Status', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead title="Claims" subtitle="Damage, quality and transit-loss claims raised by buyers." />

      {stats && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Claimed value" value={inr(stats.claimedAmount, true)} icon="⚠" foot={`${num(stats.total)} claims`} />
          <StatCard label="Open" value={num(stats.open)} icon="⏳" foot="Awaiting decision" />
          <StatCard label="Settled" value={num(stats.byStatus.settled)} icon="✓" foot={inr(stats.settledAmount, true)} />
          <StatCard label="Rejected" value={num(stats.byStatus.rejected)} icon="✕" />
          <StatCard label="Damage claims" value={num(stats.byType.damage)} icon="📦" />
          <StatCard label="Quality issues" value={num(stats.byType.quality)} icon="🔍" />
        </div>
      )}

      <Tabs tabs={TABS} value={list.filters.status} onChange={v => list.setFilter('status', v)} />

      <div className="filters">
        <SearchBox value={list.filters.search} onChange={v => list.setFilter('search', v)} placeholder="Search claim number…" />
        <select className="select" style={{ width: 200 }} value={list.filters.type} onChange={e => list.setFilter('type', e.target.value)}>
          <option value="">All claim types</option>
          <option value="damage">Damage</option>
          <option value="quality">Quality issue</option>
          <option value="manufacturing_defect">Manufacturing defect</option>
          <option value="shortage">Shortage</option>
          <option value="transit_loss">Transit loss</option>
        </select>
        <div className="grow" />
        <span className="text-muted text-sm">{list.meta ? `${num(list.meta.total)} claims` : ''}</span>
      </div>

      <DataTable columns={columns} rows={list.rows} loading={list.loading} meta={list.meta} onPage={list.setPage} onRowClick={openDetail} />

      <Modal
        open={!!detail}
        size="wide"
        title={detail?.claimNumber}
        subtitle={detail?.title}
        onClose={() => setDetail(null)}
        footer={
          detail && (
            <>
              {(NEXT[detail.status] || []).map(next => (
                <Button
                  key={next}
                  variant={next === 'rejected' ? 'danger' : 'primary'}
                  onClick={() => {
                    setAction({ claim: detail, status: next });
                    setAmount(String(detail.claimAmount));
                    setNote('');
                  }}
                >
                  Mark {next.replace('_', ' ')}
                </Button>
              ))}
              {detail.status === 'approved' && (
                <Button
                  onClick={() => {
                    setSettle(detail);
                    setSettleForm({ amount: String(detail.resolution?.approvedAmount || detail.claimAmount), mode: 'bank_transfer', reference: '' });
                  }}
                >
                  Settle claim
                </Button>
              )}
            </>
          )
        }
      >
        {detail && (
          <>
            <div className="row mb-2" style={{ gap: 8 }}>
              <Badge status={detail.status} />
              <Badge status={detail.type} tone="warn" dot={false}>{titleCase(detail.type)}</Badge>
              {detail.isInsured && <Badge status="active" dot={false}>Insured</Badge>}
            </div>

            <KV
              items={[
                { label: 'Order', value: detail.order?.orderNumber },
                { label: 'Product', value: detail.product?.name },
                { label: 'Affected qty', value: num(detail.affectedQuantity) },
                { label: 'Claimed amount', value: inr(detail.claimAmount) },
                { label: 'Raised by', value: `${detail.raisedBy?.name} · ${detail.raisedByUser?.name || ''}` },
                { label: 'Against', value: detail.against?.name },
                detail.policy && { label: 'Policy', value: `${detail.policy.policyNumber} (cover ${inr(detail.policy.coverageAmount)})` },
                detail.insurer && { label: 'Insurer', value: detail.insurer?.name },
                detail.verification?.verifiedAt && { label: 'Verified', value: `${dateTimeStr(detail.verification.verifiedAt)} ${detail.verification.surveyorName ? `by ${detail.verification.surveyorName}` : ''}` },
                detail.resolution?.decision && { label: 'Decision', value: `${titleCase(detail.resolution.decision)} — ${inr(detail.resolution.approvedAmount)}` },
                detail.settlement?.settledAt && { label: 'Settled', value: `${inr(detail.settlement.amount)} via ${detail.settlement.mode} (${detail.settlement.reference})` },
              ]}
            />

            <div className="divider" />
            <div className="card-title mb-2">Buyer's description</div>
            <p className="text-sm text-muted">{detail.description}</p>

            {(detail.images || []).length > 0 && (
              <>
                <div className="divider" />
                <div className="card-title mb-2">Evidence</div>
                <div className="row wrap">
                  {detail.images.map(url => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="Claim evidence" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--line)' }} />
                    </a>
                  ))}
                </div>
              </>
            )}

            <div className="divider" />
            <div className="card-title mb-2">History</div>
            <div className="timeline">
              {[...(detail.history || [])].reverse().map((event, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className={`tl-item ${index === 0 ? 'current' : 'done'}`}>
                  <div className="tl-title">{titleCase(event.status)}</div>
                  <div className="tl-time">{dateTimeStr(event.at)}{event.by?.name ? ` · ${event.by.name}` : ''}</div>
                  {event.note && <div className="text-sm text-muted">{event.note}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!action}
        title={`Mark claim ${action?.status.replace('_', ' ')}`}
        subtitle={action?.claim.claimNumber}
        onClose={() => setAction(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAction(null)}>Cancel</Button>
            <Button variant={action?.status === 'rejected' ? 'danger' : 'primary'} loading={busy} onClick={submitAction}>
              Confirm
            </Button>
          </>
        }
      >
        {action?.status === 'approved' && (
          <>
            <Select
              label="Resolution" value={decision} onChange={e => setDecision(e.target.value)}
              options={[
                { value: 'refund', label: 'Refund the buyer' },
                { value: 'replace', label: 'Replace the goods' },
                { value: 'credit_note', label: 'Issue a credit note' },
              ]}
            />
            <Input label="Approved amount (₹)" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
          </>
        )}
        <Textarea
          label={action?.status === 'rejected' ? 'Reason for rejection' : 'Note (sent to the buyer)'}
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </Modal>

      <Modal
        open={!!settle}
        title="Settle this claim"
        subtitle={settle?.claimNumber}
        onClose={() => setSettle(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSettle(null)}>Cancel</Button>
            <Button loading={busy} onClick={submitSettle}>Record settlement</Button>
          </>
        }
      >
        <Input label="Settlement amount (₹)" type="number" value={settleForm.amount} onChange={e => setSettleForm({ ...settleForm, amount: e.target.value })} />
        <Select
          label="Mode" value={settleForm.mode} onChange={e => setSettleForm({ ...settleForm, mode: e.target.value })}
          options={[
            { value: 'bank_transfer', label: 'Bank transfer' },
            { value: 'credit_note', label: 'Credit note' },
            { value: 'replacement', label: 'Goods replaced' },
            { value: 'insurance', label: 'Paid by insurer' },
          ]}
        />
        <Input label="Reference" value={settleForm.reference} onChange={e => setSettleForm({ ...settleForm, reference: e.target.value })} placeholder="NEFT / UTR number" />
      </Modal>
    </>
  );
};

export default Claims;
